import Api from '../api';
import {
  GET_ALL_AGENT_DETAILS, AGENT_LIST_V3_URL, ADD_BALANCE_URL,CHANGE_STATUS_URL,UPDATE_OPERATOR_URL,
  CHANGE_STATUS,EDIT_DLT,EDIT_DLT_URL,ADD_BALANCE,ADD_SENDER_ID_URL,ADD_SENDER_ID,CHANGE_DEFAULT_SENDER_ID_URL,
  CHANGE_DEFAULT_SENDER_ID,ADD_SMS_TYPE,ADD_SMS_TYPE_URL, ADD_BLOCK_NUMBER_URL, ADD_BLOCK_NUMBER,GET_BLOCK_NUMBER, CHANGE_PASSWORD_URL,
  CHANGE_PASSWORD, DELETE_SENDER_ID_URL, DELETE_SENDER_ID, GET_TENANT_DETAILS, GET_TENANT_DETAILS_URL,
  GET_TENANT_USERS, GET_TENANT_USERS_URL,
  IMPERSONATE_USER, IMPERSONATE_USER_URL,
  SEND_WELCOME_EMAIL_URL,
  ENSURE_OTP_METHODS_URL,
  LIST_USER_SESSIONS_URL, TERMINATE_USER_SESSION_URL,
  ACTIVATE_USER_URL, DEACTIVATE_USER_URL, UPDATE_TENANT_USER_URL, UPDATE_ORG_NAME_URL,
  GET_TENANT_SMS_CONFIG, GET_TENANT_SMS_CONFIG_URL,
  SET_DEFAULT_SMS_TYPE, SET_DEFAULT_SMS_TYPE_URL,
  ADMIN_LIST_TEMPLATES, ADMIN_LIST_TEMPLATES_URL,
  ADMIN_CREATE_TEMPLATE, ADMIN_UPDATE_TEMPLATE, ADMIN_DELETE_TEMPLATE,
  LIST_ORG_PROJECTS_URL,
  LIST_ONBOARDINGS_URL,
  ADMIN_LIST_API_KEYS_BY_TENANT, ADMIN_LIST_API_KEYS_BY_TENANT_URL,
  ADMIN_PROVISION_MACHINE_USER_API_KEY, ADMIN_PROVISION_MACHINE_USER_API_KEY_URL,
  ADMIN_REVOKE_API_KEY, ADMIN_REVOKE_API_KEY_URL,
  ADMIN_SEARCH_API_KEY_URL, ADMIN_SEARCH_IDENTITY_URL
} from '../lib/constant';
import {handleAPIDispatch, handleRejectCatch} from '../lib/utils';

// Default SMS types
const DEFAULT_SMS_TYPES = ['otp', 'transactional', 'promotional'];

// Helper to transform balances array to smsCount object
function transformBalancesToSmsCount(balances) {
  if (!balances || !Array.isArray(balances) || balances.length === 0) {
    return {};
  }
  // Sum up balances from all workspaces
  let totalTrans = 0, totalOtp = 0, totalPromo = 0;
  balances.forEach(wb => {
    totalTrans += parseInt(wb.transactional) || 0;
    totalOtp += parseInt(wb.otp) || 0;
    totalPromo += parseInt(wb.promotional) || 0;
  });
  return {
    transactional: totalTrans,
    otp: totalOtp,
    promotional: totalPromo
  };
}

// Strip the ORGANIZATION_STATE_ / ACCOUNT_STATUS_ prefixes so the table
// cells render short, honest labels (Active / Inactive / Trial / Blocked
// / Terminated) without inventing business semantics the backend doesn't
// promise. UNSPECIFIED + empty render as '--'.
function shortStateLabel(raw) {
  if (!raw) return '';
  const upper = raw.toUpperCase();
  if (upper === 'ORGANIZATION_STATE_UNSPECIFIED' || upper === 'ACCOUNT_STATUS_UNSPECIFIED') return '';
  if (upper.startsWith('ORGANIZATION_STATE_')) {
    return upper.slice('ORGANIZATION_STATE_'.length).toLowerCase().replace(/^./, c => c.toUpperCase());
  }
  if (upper.startsWith('ACCOUNT_STATUS_')) {
    return upper.slice('ACCOUNT_STATUS_'.length).toLowerCase().replace(/^./, c => c.toUpperCase());
  }
  // DB-form lowercase already (trial/active/blocked/terminated)
  return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
}

// Transform Zitadel organizations response to legacy institutes format.
//
// Per ADR_TENANT_LIST_LIVE_IDENTITY phase 2 the backend now stamps each
// OrganizationV2 with two layers:
//   - contactName / contactEmail — LIVE from zitadel (empty when the
//     batched ListUsersByIDs failed or the org has no onboarded user)
//   - onboarding — the FROZEN signup snapshot from tenants.signup_*
//     (orgName, contactName, contactEmail, contactPhone, signedUpAt)
//
// We forward both verbatim so the AdminPanel cell renderers can apply
// the fallback chain: live → onboarding snapshot → '--'.
// balanceCredits arrives as proto3 map<string,int64> → JSON object with
// STRING values (e.g. {"transactional":"2000"}). Coerce to numbers at this
// adapter boundary — same bug class as getContactGroup's "1"+0+0="100"
// int64-string concatenation — so the table only ever sees numbers.
// Absent / non-object → null (cells render '--').
function coerceBalanceCredits(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const credits = {};
  Object.keys(raw).forEach((key) => {
    credits[key] = Number(raw[key]) || 0;
  });
  return credits;
}

function transformOrgsToInstitutes(orgs) {
  return orgs.map(org => ({
    instituteId: org.id,
    id: org.id,
    name: org.name,
    tenantName: org.name,
    userId: org.primaryDomain || org.name,
    info: org.name,
    contactName: org.contactName || org.onboarding?.contactName || '',
    contactEmail: org.contactEmail || org.onboarding?.contactEmail || '',
    domainName: org.primaryDomain || '',
    onboarding: org.onboarding || null,
    // Two independent lifecycle axes — see docs/ADR_TENANT_STATUS_AND_TIER.md.
    // iamState: raw zitadel ORGANIZATION_STATE_* — IDENTITY layer (login on/off)
    // accountStatus: trial/active/blocked/terminated — BUSINESS layer
    // rateLimitTier: operational SLA tier (RLQS)
    iamState: org.iamState || org.state || '',
    accountStatus: org.accountStatus || '',
    rateLimitTier: org.rateLimitTier || '',
    // status — legacy field, kept for callers that still read it; now sourced
    // from accountStatus (the BUSINESS-layer truth) instead of the misleading
    // zitadel-state-rebranded-as-'live'/'testing' it used to be.
    status: (org.accountStatus || '').toLowerCase() || 'unknown',
    smsCount: {},
    smsTypeTaken: [],
    // Enriched ListOrganizationsV2 fields (tenant_v2_service.proto):
    //   defaultSenderId — tenant's default SMS header ('' when unset)
    //   balanceCredits  — whole credits per wallet message type
    //     (promotional/transactional/otp, sometimes general), coerced to
    //     numbers above; null when the backend omitted the object.
    defaultHeader: org.defaultSenderId || '',
    balanceCredits: coerceBalanceCredits(org.balanceCredits),
    senderId: org.defaultSenderId || '',
    senderIds: [],
    mobile: org.onboarding?.contactPhone ||'',
    email: '',
    createdAt: org.createdAt || '',
  }));
}

export {shortStateLabel};

// Safety cap on the fetch-all loop: 50 pages × 200/page = 10k orgs.
const MAX_ORG_PAGES = 50;

// Get all agents/tenants from Zitadel TenantV2 API.
//
// Backend returns { success, data: { organizations, total, page, pageSize } }
// with a server-side page-size cap of 500 (v3; raised from 200 — browse
// pages are also served from a 10s server cache). The AdminPanel table wants
// the full list (it filters/paginates client-side), so this action loops
// pages sequentially until `total` rows are collected — 2 round-trips at
// ~900 tenants — then dispatches ONCE in the same payload shape the view
// already consumes: a flat `institutes` list plus `pagination` with the
// server total.
export function getAllAgent({ pageSize = 500, search = '' } = {}){
  const fetchPage = (page) => {
    const params = new URLSearchParams({
      page: String(page),
      pageSize: String(pageSize),
    });
    if (search) params.set('search', search);
    return Api.get(`${AGENT_LIST_V3_URL}?${params.toString()}`);
  };
  return (dispatch) => {
    (async () => {
      let allOrgs = [];
      let total = 0;
      let success = false;
      for (let page = 1; page <= MAX_ORG_PAGES; page++) {
        const response = await fetchPage(page);
        const body = response.data || {};
        const data = body.data || {};
        const orgs = data.organizations || [];
        success = !!body.success;
        allOrgs = allOrgs.concat(orgs);
        total = data.total ?? allOrgs.length;
        // Stop when the server total is reached, or on an empty/short page
        // (defensive: never spin on a backend that misreports total).
        if (allOrgs.length >= total || orgs.length < pageSize) break;
      }
      dispatch({
        type: GET_ALL_AGENT_DETAILS,
        payload: {
          success,
          data: {
            institutes: transformOrgsToInstitutes(allOrgs),
            pagination: { total, page: 1, pageSize: allOrgs.length || pageSize },
          },
        },
      });
    })().catch((err) => {
      console.error('getAllAgent error:', err);
      dispatch({ type: GET_ALL_AGENT_DETAILS, payload: { success: false, data: { institutes: [], pagination: { total: 0, page: 1, pageSize } } } });
    });
  };
}

// listOrgsByAccountStatus loads only the orgs in one account status (trial,
// active, testing, blocked, terminated), newest first. The filter runs in
// SQL (ListOrganizationsV3 account_status), so the admin panel's Account
// Status tab does not fetch every org the way getAllAgent does.
export async function listOrgsByAccountStatus(accountStatus, {pageSize = 500} = {}) {
  let orgs = [];
  for (let page = 1; page <= MAX_ORG_PAGES; page++) {
    const params = new URLSearchParams({
      page: String(page),
      pageSize: String(pageSize),
      accountStatus,
      orderBy: 'created_at'
    });
    const resp = await Api.get(`${AGENT_LIST_V3_URL}?${params.toString()}`);
    const data = resp?.data?.data || {};
    const rows = data.organizations || [];
    orgs = orgs.concat(rows);
    if (orgs.length >= (data.total ?? orgs.length) || rows.length < pageSize) break;
  }
  return transformOrgsToInstitutes(orgs);
}

// Look an org up by a single value — org id or contact email.
//
// No dedicated endpoint: ListOrganizationsV3's `search` already matches org id
// (exact), org name, contact name and the SIGNUP contact email/phone in one
// indexed query. A separate reverse-lookup would be a second way to ask a
// question the list already answers — unlike the API-key finder, which needs
// one because it matches a SHA256 hash no list column carries.
//
// Returns the raw organizations array (usually 0 or 1 rows).
export async function adminLookupOrgs(query, {pageSize = 25} = {}) {
  const params = new URLSearchParams({
    page: '1',
    pageSize: String(pageSize),
    search: query
  });
  const resp = await Api.get(`${AGENT_LIST_V3_URL}?${params.toString()}`);
  return resp?.data?.data?.organizations || [];
}

// Get tenant details for the admin drawer.
// 2026-09-08: served from combird's own DB via /api/v3/organizations/{id}
// (GetOrganizationV3). The previous /api/v2/organizations/{id} asked zitadel
// to list every org to find one — ~2.1 s per click, growing with every
// customer. Same response shape: { success, data: OrganizationV2 }.
export function getTenantDetails(tenantId){
  const request = Api.get(`${AGENT_LIST_V3_URL}/${tenantId}`);
  return (dispatch) => {
    request.then((response) => {
      const body = response.data || {};
      const org = body.data || body.organization || body;
      const payload = {
        success: true,
        data: {
          instituteId: org.id || tenantId,
          id: org.id || tenantId,
          name: org.name || '',
          tenantName: org.name || '',
          userId: org.primaryDomain || org.name || '',
          info: org.name || '',
          iamState: org.iamState || org.state || '',
          accountStatus: org.accountStatus || '',
          rateLimitTier: org.rateLimitTier || '',
          status: (org.accountStatus || '').toLowerCase() || 'unknown',
          smsCount: {},
          smsTypeTaken: [],
          senderId: '',
          senderIds: [],
          mobile: '',
          email: '',
          createdAt: org.createdAt || '',
        }
      };
      dispatch({ type: GET_TENANT_DETAILS, payload });
    }).catch((err) => {
      console.error('getTenantDetails error:', err);
      dispatch({ type: GET_TENANT_DETAILS, payload: { success: false, data: {} } });
    });
  };
}

// Get tenant SMS config (sender IDs + message types) for admin panel
export function getTenantSmsConfig(tenantId) {
  const url = GET_TENANT_SMS_CONFIG_URL.replace('{tenantId}', tenantId);
  const request = Api.get(url);
  return handleAPIDispatch(request, GET_TENANT_SMS_CONFIG);
}

export function addBalance(instituteId,params){
  const smsTypeMap = {
    'PROMO': 'promotional',
    'TRANS': 'transactional',
    'OTP': 'otp'
  };
  let amount = parseInt(params.smsCount);
  if (params.operation === 'subtract') {
    amount = -Math.abs(amount);
  }
  const apiParams = {
    workspace_id: instituteId + '-default',
    message_type: smsTypeMap[params.smsType] || params.smsType.toLowerCase(),
    amount: amount,
    reason: params.notes || 'Admin balance modification',
    reference_id: ''
  };
  const request = Api.post(ADD_BALANCE_URL.replace('{instId}',instituteId), apiParams);
  return handleAPIDispatch(request, ADD_BALANCE)
}

export function addSMSType(instituteId,params){
  // Transform params for new API format
  // Frontend sends: { smsType: ["promotional", "transactional"], defaultSmsType: "promotional" }
  // API expects: { sms_types: ["promotional", "transactional"], default_sms_type: "promotional" }
  const apiParams = {
    sms_types: params.smsType || [],
    default_sms_type: params.defaultSmsType || ''
  };
  const request = Api.put(ADD_SMS_TYPE_URL.replace('{instId}',instituteId), apiParams);
  return handleAPIDispatch(request, ADD_SMS_TYPE)
}

export function changeStatus(instituteId,params){
  const request = Api.put(CHANGE_STATUS_URL.replace('{instId}',instituteId), params);
  return handleAPIDispatch(request, CHANGE_STATUS)
}


export function editDLT(instituteId,params) {
  // Transform camelCase to snake_case for new API
  const apiParams = {
    dlt_principal_entity_id: params.dltPrincipalEntityId || ""
  };
  const request = Api.put(EDIT_DLT_URL.replace("{instId}",instituteId), apiParams);
  return handleAPIDispatch(request, EDIT_DLT)
}

export function addSenderId(instituteId,params){
  // Transform camelCase to snake_case for new API.
  // No TMD here: UpdateSenderIdsRequest carries only sender_ids — new
  // headers start in TMD mode 'direct'; set TMD/mode via the pencil after.
  const apiParams = {
    sender_ids: params.senderIds || params.sender_ids || []
  };
  const request = Api.put(ADD_SENDER_ID_URL.replace("{instId}",instituteId), apiParams);
  return handleAPIDispatch(request, ADD_SENDER_ID)
}

export function changeDefaultSenderId(instituteId,params){
  // Transform camelCase to snake_case for new API
  const apiParams = {
    sender_id: params.senderId || params.sender_id || ""
  };
  const request = Api.put(CHANGE_DEFAULT_SENDER_ID_URL.replace("{instId}",instituteId), apiParams);
  return handleAPIDispatch(request, CHANGE_DEFAULT_SENDER_ID)
}

export function setDefaultSmsType(instituteId, smsType){
  const apiParams = {
    sms_type: smsType
  };
  const request = Api.put(SET_DEFAULT_SMS_TYPE_URL.replace("{instId}", instituteId), apiParams);
  return handleAPIDispatch(request, SET_DEFAULT_SMS_TYPE)
}

export function addBlockNo(params){
  const request = Api.post(ADD_BLOCK_NUMBER_URL, params);
  return handleAPIDispatch(request, ADD_BLOCK_NUMBER)
}

export function getBlockNumber(){
  return (dispatch) => {
    dispatch({
      type: GET_BLOCK_NUMBER,
      payload: { success: true, data: { blockNumbers: [] } }
    });
  };
}

export function getBlockNumberOriginal(){
  const request = Api.get(ADD_BLOCK_NUMBER_URL);
  return handleAPIDispatch(request, GET_BLOCK_NUMBER)
}

export function changePassword(params){
  /// New API: POST /api/v1/account/change-password with { old_password, new_password }
  const apiParams = { old_password: params.oldPassword || '', new_password: params.password || '' };
  const request = Api.put(CHANGE_PASSWORD_URL, apiParams);
  return handleAPIDispatch(request, CHANGE_PASSWORD)
}

export function deleteSenderId(senderId, instituteId) {
  let url= DELETE_SENDER_ID_URL.replace('{instId}',instituteId)
  url= url.replace('{senderid}',senderId)
  const request = Api.delete(url);
  return handleAPIDispatch(request, DELETE_SENDER_ID)
}

// Get users for a tenant/organization from Zitadel
export function getTenantUsers(tenantId){
  const url = GET_TENANT_USERS_URL.replace('{tenantId}', tenantId);
  const request = Api.get(url);
  return (dispatch) => {
    request.then((response) => {
      dispatch({ type: GET_TENANT_USERS, payload: response.data });
    }).catch((err) => {
      console.error('getTenantUsers error:', err);
      dispatch({ type: GET_TENANT_USERS, payload: { success: false, data: { users: [] } } });
    });
  };
}

// =========================================================================
// Admin Template Management
// =========================================================================

export function adminListTemplates(tenantId) {
  const url = ADMIN_LIST_TEMPLATES_URL.replace('{tenantId}', tenantId);
  const request = Api.get(url);
  return handleAPIDispatch(request, ADMIN_LIST_TEMPLATES);
}

// There is no DLT category anywhere (removed 2026-08-01) — the compliance
// class follows the sender's header type. The caller passes isPromotional
// (numeric-header sender) so this builder can attach the promotional-only
// extras: the industry category (mandatory per TRAI since 14 Jul 2026) rides
// in the metadata map, and promotional needs a consent_template_id — fall
// back to the DLT template id when the admin form doesn't collect one.
function buildAdminTemplateParams(params) {
  const dltTemplateId = params.dltTemplateId || params.dlt_template_id;
  const needsConsent = !!params.isPromotional;
  const metadata = {};
  if (params.financialInstituteName) {
    metadata.financial_institute_name = params.financialInstituteName;
  }
  if (params.industryCategory) {
    metadata.industry_category = params.industryCategory;
  }
  return {
    template_name: params.templateName || params.template_name,
    template_text: params.templateText || params.template_text,
    sender_id_ref: params.senderIdRef || params.sender_id_ref,
    dlt_template_id: dltTemplateId,
    metadata: Object.keys(metadata).length ? metadata : undefined,
    consent_template_id: needsConsent
      ? params.consentTemplateId || dltTemplateId
      : undefined,
    variables: params.variables || [],
  };
}

export function adminCreateTemplate(tenantId, params) {
  const url = ADMIN_LIST_TEMPLATES_URL.replace('{tenantId}', tenantId);
  const request = Api.post(url, buildAdminTemplateParams(params));
  return handleAPIDispatch(request, ADMIN_CREATE_TEMPLATE);
}

export function adminUpdateTemplate(tenantId, templateId, params) {
  const url = ADMIN_LIST_TEMPLATES_URL.replace('{tenantId}', tenantId) + '/' + templateId;
  const request = Api.put(url, buildAdminTemplateParams(params));
  return handleAPIDispatch(request, ADMIN_UPDATE_TEMPLATE);
}

export function adminDeleteTemplate(tenantId, templateId) {
  const url = ADMIN_LIST_TEMPLATES_URL.replace('{tenantId}', tenantId) + '/' + templateId;
  const request = Api.delete(url);
  return handleAPIDispatch(request, ADMIN_DELETE_TEMPLATE);
}

// =========================================================================
// Admin API Keys (machine user / PAT) — onboarding_admin.proto:
//   AdminListApiKeysByTenant + ProvisionMachineUserApiKey.
// Used by the AdminPanel customer-detail modal's "API Keys" tab and the
// "Generate API Key" CTA on the onboarding completion screen.
// =========================================================================

// List all API keys for one tenant. Optional channel filter; default
// returns keys across every channel. Includes revoked rows — the UI
// filters client-side when only active ones should show.
export function adminListApiKeysByTenant(tenantId, project) {
  let url = ADMIN_LIST_API_KEYS_BY_TENANT_URL.replace('{tenantId}', tenantId);
  if (project) {
    url += `?project=${encodeURIComponent(project)}`;
  }
  const request = Api.get(url);
  return handleAPIDispatch(request, ADMIN_LIST_API_KEYS_BY_TENANT);
}

// Provision (first-time) or rotate (replace_existing=true) a machine
// user + PAT for one (tenant, project). Response carries the one-time
// plaintext_token — the UI MUST surface it once with a copy button;
// the customer cannot retrieve the cleartext PAT again from Zitadel.
//
// opts: { projectId?, keyName?, description?, replaceExisting? }
//   - projectId: omit on first call; server resolves via Zitadel
//   - replaceExisting: true → revoke prior key, mint a new one
export function adminProvisionMachineUserApiKey(tenantId, project, opts = {}) {
  const url = ADMIN_PROVISION_MACHINE_USER_API_KEY_URL.replace('{tenantId}', tenantId);
  const apiParams = {
    tenant_id: tenantId,
    project,
    project_id: opts.projectId || undefined,
    key_name: opts.keyName || 'default',
    description: opts.description || undefined,
    replace_existing: !!opts.replaceExisting,
  };
  const request = Api.post(url, apiParams);
  return handleAPIDispatch(request, ADMIN_PROVISION_MACHINE_USER_API_KEY);
}

// Revoke a single API key for a tenant (overlap rotation: after a 2nd key
// is generated and the integration migrated, revoke the older one — zero
// downtime). Backend: AdminRevokeApiKey POST .../api_keys/{id}/revoke.
// Reuses the existing apikeyServer.RevokeApiKey under the hood.
export function adminRevokeApiKey(tenantId, apiKeyId) {
  const url = ADMIN_REVOKE_API_KEY_URL
    .replace('{tenantId}', tenantId)
    .replace('{id}', apiKeyId);
  const request = Api.post(url, {tenant_id: tenantId, id: apiKeyId});
  return handleAPIDispatch(request, ADMIN_REVOKE_API_KEY);
}

// Admin reverse lookup — OnboardingAdminService.AdminSearchApiKey:
// resolve a full plaintext API key to its owning tenant. POST body (never
// a URL — the key is a secret); server matches by SHA256 hash, so only
// the complete key resolves. Revoked keys still resolve (key.isActive =
// false) so support can explain why a key stopped working. Promise-
// returning (not Redux-dispatched): the Search tab is the only consumer
// and correlates request → response locally.
export function adminSearchApiKey(apiKeyToken) {
  return Api.post(ADMIN_SEARCH_API_KEY_URL, {api_key: apiKeyToken});
}

// OnboardingAdminService.AdminSearchIdentity — search ZITADEL, not our tables.
//
// Answers what the org list cannot: a login email/phone lives in zitadel, one
// row per user, and an org routinely has more than one — so a customer who
// changed their address after signup is invisible to the V3 list and found
// here. Pass exactly one of {email, phone, orgId}; the server rejects zero or
// two, because inferring which was meant from the string shape is how a phone
// number becomes an org-id lookup that silently finds nothing.
export async function adminSearchIdentity({email, phone, orgId} = {}) {
  const body = {};
  if (email) body.email = email;
  if (phone) body.phone = phone;
  if (orgId) body.org_id = orgId;
  const resp = await Api.post(ADMIN_SEARCH_IDENTITY_URL, body);
  return resp?.data?.matches || [];
}

// TenantV2Service.GetOrganizationProjectSummary — projects (Zitadel
// projects) owned by an org. Lazy-fetched from the customer-detail
// modal's Projects tab; not dispatched through Redux because the data
// is tab-local and the modal unmounts on close.
export function fetchOrgProjects(orgId){
  const url = LIST_ORG_PROJECTS_URL.replace('{orgId}', encodeURIComponent(orgId));
  return Api.get(url);
}

// Impersonate a user via token exchange
export function impersonateUser(targetUserId, targetOrgId){
  const request = Api.post(IMPERSONATE_USER_URL, {
    target_user_id: targetUserId,
    target_org_id: targetOrgId
  });
  return (dispatch) => {
    request.then((response) => {
      dispatch({ type: IMPERSONATE_USER, payload: response.data });
    }).catch((err) => {
      console.error('impersonateUser error:', err);
      dispatch({ type: IMPERSONATE_USER, payload: { success: false, error: err.message || 'Impersonation failed' } });
    });
  };
}

// Support-triggered welcome email (the only account email left after the
// OTP-only cutover — replaces the retired reset-password/invite emails).
// Promise-returning like the other per-user actions — the confirm dialog
// owns its own local state. Success: {audit_id, masked_recipient}; 429 =
// 5-min cooldown per (tenant, user, template) still active.
// POST /api/v1/admin/tenants/{tenant_id}/users/{user_id}/welcome-email
export function sendWelcomeEmail(tenantId, userId){
  const url = SEND_WELCOME_EMAIL_URL
    .replace('{tenantId}', encodeURIComponent(tenantId))
    .replace('{userId}', encodeURIComponent(userId));
  return Api.post(url, {});
}

// MANDATE v2 (OTP-only login): register zitadel OTP auth methods on a user
// (or the whole org when userId is omitted) so code login works. Idempotent;
// backend skips machine users and unverified channels with reasons.
// POST /api/v1/admin/tenants/{tenantId}/otp-methods/ensure  body {user_id?}
export function ensureOtpMethods(tenantId, userId){
  const url = ENSURE_OTP_METHODS_URL
    .replace('{tenantId}', encodeURIComponent(tenantId));
  return Api.post(url, userId ? {user_id: userId} : {});
}

// User session management (zitadel session v2). Promise-returning like the
// account emails — the sessions dialog owns its own local state. List
// returns {sessions: [{sessionId, creationDate, changeDate, expirationDate,
// userAgent, ip}]}; terminate is idempotent (backend maps zitadel NotFound
// to success — the session being gone is the desired end state).
export function listUserSessions(tenantId, userId){
  const url = LIST_USER_SESSIONS_URL
    .replace('{tenantId}', encodeURIComponent(tenantId))
    .replace('{userId}', encodeURIComponent(userId));
  return Api.get(url);
}

export function terminateUserSession(tenantId, userId, sessionId){
  const url = TERMINATE_USER_SESSION_URL
    .replace('{tenantId}', encodeURIComponent(tenantId))
    .replace('{userId}', encodeURIComponent(userId))
    .replace('{sessionId}', encodeURIComponent(sessionId));
  return Api.delete(url);
}

// Per-user IAM activate/deactivate. Promise-returning (no redux state) —
// caller awaits + reads .data.user_state to refresh the row's local copy
// without a full re-list. Identity-layer ops (zitadel UserState), distinct
// from the tenant-level changeStatus() above which flips billing's
// account_status. See docs/ADR_TENANT_STATUS_AND_TIER.md.
//
// Backend: PUT /api/v1/support/tenants/{tenant_id}/users/{user_id}/{activate,deactivate}
// — see status_admin_handler.go. Idempotent on either side
// (FailedPrecondition for already-{active,inactive} → translated to 200).
export function activateTenantUser(tenantId, userId) {
  const url = ACTIVATE_USER_URL
    .replace('{tenantId}', encodeURIComponent(tenantId))
    .replace('{userId}', encodeURIComponent(userId));
  return Api.put(url, {});
}

export function deactivateTenantUser(tenantId, userId, reason) {
  const url = DEACTIVATE_USER_URL
    .replace('{tenantId}', encodeURIComponent(tenantId))
    .replace('{userId}', encodeURIComponent(userId));
  return Api.put(url, reason ? {reason} : {});
}

// Per-user profile edit. `fields` is a partial { full_name?, email?, phone? };
// only the keys present are sent (each is optional server-side — omitted =
// leave alone, empty phone = clear). Returns the Api.put promise so the
// caller can await + refresh the Users list. Changing email does NOT change
// the login username (frozen at user creation). Backend: UpdateTenantUser
// PUT /api/v1/support/tenants/{tenant_id}/users/{user_id} — support_handler.go.
// Business-name rename. Backend: UpdateOrgName
// PUT /api/v1/support/orgs/{org_id}/org_name — writes the zitadel org name
// (what the customer's token and screens show) and mirrors tenants.name
// (what this admin list reads) in one call. Allowed for platform_admin,
// admin and platform_support. Resolves with {old_org_name, org_name}.
export function updateOrgName(orgId, name, reason) {
  const url = UPDATE_ORG_NAME_URL.replace('{orgId}', encodeURIComponent(orgId));
  const body = { org_name: name };
  if (reason) body.reason = reason;
  return Api.put(url, body).then(r => r?.data?.data || r?.data || {});
}

export function updateTenantUser(tenantId, userId, fields) {
  const url = UPDATE_TENANT_USER_URL
    .replace('{tenantId}', encodeURIComponent(tenantId))
    .replace('{userId}', encodeURIComponent(userId));
  const body = {};
  if (fields.full_name !== undefined) body.full_name = fields.full_name;
  if (fields.email !== undefined) body.email = fields.email;
  if (fields.phone !== undefined) body.phone = fields.phone;
  return Api.put(url, body);
}
